import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, Database, Cloud, Key, Bell, Activity, AlertTriangle, CheckCircle2, XCircle,
  Users, FileText, HeartPulse
} from "lucide-react";

const STATUS_COLOR = {
  OK: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  WARNING: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  ERROR: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
};

function StatusIcon({ status }) {
  if (status === "ok") return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  if (status === "warning") return <AlertTriangle className="w-4 h-4 text-amber-600" />;
  return <XCircle className="w-4 h-4 text-rose-600" />;
}

export default function SystemHealthTab() {
  const [data, setData] = useState(null);
  const [schedulers, setSchedulers] = useState(null);
  const [monitor, setMonitor] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [env, sch, mon] = await Promise.all([
        api.get("/admin/env-check"),
        api.get("/admin/schedulers").catch(() => ({ data: { schedulers: [] } })),
        api.get("/ops/system-monitor").catch(() => ({ data: null })),
      ]);
      setData(env.data);
      setSchedulers(sch.data);
      setMonitor(mon.data);
    } catch (e) {
      toast.error("Erreur lors de la vérification");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const overall = data?.overall;
  const color = overall ? STATUS_COLOR[overall] : STATUS_COLOR.WARNING;
  const checks = data?.checks || [];
  const hiddenExternalChecks = new Set([
    "RESEND_API_KEY",
    "RESEND_SENDER_EMAIL",
    "TWILIO_ACCOUNT_SID",
    "TWILIO_AUTH_TOKEN",
    "TWILIO_VERIFY_SERVICE_SID",
    "TWILIO_PHONE_NUMBER",
    "INFOBIP_API_KEY",
    "INFOBIP_BASE_URL",
  ]);
  const usefulChecks = checks.filter((item) => !hiddenExternalChecks.has(item.name));
  const checksByName = Object.fromEntries(usefulChecks.map((item) => [item.name, item]));
  const frontendSentryConfigured = Boolean(process.env.REACT_APP_SENTRY_DSN);
  const frontendChecks = [{
    name: "REACT_APP_SENTRY_DSN",
    status: frontendSentryConfigured ? "ok" : "warning",
    message: frontendSentryConfigured ? "présent dans le build React" : "absent du build React",
    hint: "Sentry frontend ne dépend pas du backend",
  }];
  const sentryStatus = checksByName.SENTRY_DSN?.status === "ok" && frontendSentryConfigured ? "ok" : "warning";

  const schList = schedulers?.schedulers || [];
  const schOverdue = schList.filter(s => s.isOverdue).length;
  const schError = schList.filter(s => s.lastStatus === "error").length;
  const schStatus = schError ? "error" : (schOverdue ? "warning" : (schList.length ? "ok" : "unknown"));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            État du système
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Vérification manuelle de la config prod et de l'état runtime. Aucune donnée sensible n'est exposée.
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          data-testid="btn-refresh-system-health"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {data ? "Rafraîchir" : "Vérifier l'état du système"}
        </button>
      </div>

      {!data && !loading && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-12 text-center">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Clique sur "Vérifier l'état du système" pour lancer le check.</p>
        </div>
      )}

      {!data && loading && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-12 text-center" data-testid="system-health-loading">
          <RefreshCw className="w-10 h-10 text-slate-300 mx-auto mb-3 animate-spin" />
          <p className="text-slate-500">Lecture des métriques réelles...</p>
        </div>
      )}

      {data && (
        <>
          {/* Bandeau global */}
          <div
            data-testid="system-health-overall"
            className={`rounded-xl border-2 ${color.border} ${color.bg} p-6 flex items-center gap-4`}
          >
            <div className={`w-4 h-4 rounded-full ${color.dot}`} />
            <div className="flex-1">
              <div className={`text-2xl font-bold ${color.text}`} data-testid="system-health-overall-status">{overall}</div>
              <div className="text-sm text-slate-600 mt-1" data-testid="system-health-overall-counts">
                {data.errorCount} erreur(s), {data.warningCount} avertissement(s)
              </div>
            </div>
          </div>

          {monitor && (
            <section className="space-y-3" data-testid="system-live-monitor-section">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <Database className="w-4 h-4 text-emerald-600" /> Réalité MongoDB live
                  </h3>
                  <p className="text-xs text-slate-500">
                    Source : {monitor.source === "mongodb_live" ? "MongoDB + runtime applicatif" : monitor.source} · {new Date(monitor.generatedAt).toLocaleString("fr-FR")}
                  </p>
                </div>
                {monitor.ops?.operationalAlerts?.length > 0 && (
                  <span className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="system-live-alert-count">
                    {monitor.ops.operationalAlerts.length} signalement(s)
                  </span>
                )}
              </div>

              {monitor.ops?.operationalAlerts?.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1" data-testid="system-live-alerts">
                  {monitor.ops.operationalAlerts.map((alert, index) => (
                    <p key={`${alert.level}-${index}`} className="text-xs text-amber-900" data-testid={`system-live-alert-${index}`}>• {alert.label}</p>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <LiveMetricCard icon={Users} label="Patients actifs" value={monitor.metrics?.patients?.active ?? 0} sub={`${monitor.metrics?.patients?.total ?? 0} total`} testid="live-patients-active" />
                <LiveMetricCard icon={HeartPulse} label="Check-ins 24h" value={monitor.metrics?.checkins?.last24h ?? 0} sub={`${monitor.metrics?.checkins?.last7d ?? 0} sur 7j`} testid="live-checkins-24h" />
                <LiveMetricCard icon={AlertTriangle} label="Alertes ouvertes" value={monitor.metrics?.alerts?.open ?? 0} sub={`${monitor.metrics?.alerts?.openCritical ?? 0} critique(s)`} tone={(monitor.metrics?.alerts?.openCritical ?? 0) > 0 ? "warning" : "normal"} testid="live-alerts-open" />
                <LiveMetricCard icon={FileText} label="Docs générés" value={monitor.metrics?.documents?.generated ?? 0} sub={`${monitor.metrics?.documents?.generatedLast7d ?? 0} sur 7j`} testid="live-documents-generated" />
                <LiveMetricCard icon={Activity} label="RWE 24h" value={monitor.metrics?.rwe?.eventsLast24h ?? 0} sub={`${monitor.metrics?.rwe?.eventsTotal ?? 0} total`} testid="live-rwe-24h" />
                <LiveMetricCard icon={Key} label="401 auth 1h" value={monitor.metrics?.auth?.auth401LastHour ?? 0} sub={`${monitor.metrics?.auth?.auth401Last24h ?? 0} sur 24h`} tone={(monitor.metrics?.auth?.auth401LastHour ?? 0) >= 10 ? "warning" : "normal"} testid="live-auth-401" />
                <LiveMetricCard icon={Cloud} label="Backup" value={monitor.ops?.backup?.status === "ok" ? "OK" : "À traiter"} sub={monitor.ops?.backup?.configured ? "configuré" : "non configuré"} tone={monitor.ops?.backup?.status === "ok" ? "normal" : "warning"} testid="live-backup-status" />
                <LiveMetricCard icon={Database} label="Stockage local" value={`${monitor.ops?.storage?.fileCount ?? 0}`} sub={`${monitor.ops?.storage?.sizeMb ?? 0} MB`} tone={monitor.ops?.storage?.status === "ok" ? "normal" : "warning"} testid="live-local-storage" />
              </div>
            </section>
          )}

          {/* Bandeau Version / Déploiement */}
          {data.deployment && (
            <div
              data-testid="system-health-deployment"
              className="rounded-xl p-4 flex items-center gap-3 bg-slate-50 border border-slate-200 text-sm"
            >
              <Activity className="w-5 h-5 text-slate-500" />
              <div className="flex-1 flex flex-wrap gap-x-6 gap-y-1">
                <span className="text-slate-700">
                  <span className="font-semibold">Version :</span>{" "}
                  <code className="font-mono text-xs bg-white px-2 py-0.5 rounded border border-slate-200">
                    {data.deployment.version || "unknown"}
                  </code>
                </span>
                <span className="text-slate-700">
                  <span className="font-semibold">Déployé le :</span>{" "}
                  <span className="font-mono text-xs" data-testid="system-health-boot-at">
                    {data.deployment.bootAt
                      ? new Date(data.deployment.bootAt).toLocaleString("fr-FR", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "?"}
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Bandeau Mode */}
          <div
            data-testid="system-health-mode"
            className={`rounded-xl p-4 flex items-center gap-3 ${
              data.prodMode
                ? "bg-emerald-50 border border-emerald-200"
                : "bg-amber-50 border border-amber-200"
            }`}
          >
            {data.prodMode ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-emerald-800" data-testid="system-health-prod-mode">✅ Mode production actif</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-800" data-testid="system-health-preview-mode">⚠️ Tu es en sandbox / preview</span>
              </>
            )}
            {checksByName.SANDBOX_MODE?.message?.includes("true") && (
              <span className="ml-auto text-xs text-amber-700 font-medium">SANDBOX_MODE = true</span>
            )}
          </div>

          {/* Cards par catégorie utile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <HealthCard icon={Database} title="MongoDB live" status={checksByName.MongoDB?.status || "unknown"} detail={checksByName.MongoDB?.message || ""} sub="Base active utilisée par l'app" testid="health-db" />
            <HealthCard icon={Key} title="Auth Hana" status={checksByName.JWT_SECRET?.status || "unknown"} detail={checksByName.JWT_SECRET?.message || ""} sub="Sessions + codes professionnels" testid="health-auth" />
            <HealthCard icon={Bell} title="PWA Push" status={checksByName.VAPID_PUBLIC_KEY?.status === "ok" && checksByName.VAPID_PRIVATE_KEY?.status === "ok" ? "ok" : "warning"} detail={`${checksByName.VAPID_PUBLIC_KEY?.message || "?"} · ${checksByName.VAPID_PRIVATE_KEY?.message || "?"}`} sub={`${monitor?.metrics?.push?.subscriptions ?? 0} abonnement(s) push réel(s)`} testid="health-pwa" />
            <HealthCard icon={Activity} title="Sentry" status={sentryStatus} detail={`Backend : ${checksByName.SENTRY_DSN?.message || "inconnu"} · Frontend : ${frontendChecks[0].message}`} sub="Monitoring backend + frontend" testid="health-sentry" />
            <HealthCard icon={Cloud} title="Backup" status={monitor?.ops?.backup?.status || "warning"} detail={monitor?.ops?.backup?.detail || "Non vérifié"} sub={monitor?.ops?.backup?.configured ? "configuré" : "non configuré"} testid="health-backup" />
            <HealthCard icon={FileText} title="Stockage fichiers" status={monitor?.ops?.storage?.status || "warning"} detail={monitor?.ops?.storage?.detail || "Non vérifié"} sub={`${monitor?.ops?.storage?.fileCount ?? 0} fichier(s) · ${monitor?.ops?.storage?.sizeMb ?? 0} MB`} testid="health-storage" />
            <HealthCard icon={Activity} title="Schedulers" status={schStatus} detail={schList.length === 0 ? "aucune donnée" : `${schList.length} scheduler(s) · ${schOverdue} en retard · ${schError} en erreur`} sub={schList.slice(0, 3).map(s => `${s.name}: ${s.lastStatus}`).join(" · ") || ""} testid="health-schedulers" />
            <HealthCard icon={ShieldCheck} title="RWE / Audit trail" status={(monitor?.metrics?.rwe?.eventsTotal ?? 0) > 0 ? "ok" : "warning"} detail={`${monitor?.metrics?.rwe?.eventsTotal ?? 0} événement(s) RWE · ${monitor?.metrics?.rwe?.withClinicalContext ?? 0} avec contexte`} sub={`dernier : ${monitor?.metrics?.rwe?.latestAt || "aucun"}`} testid="health-rwe" />
          </div>

          {/* Détail brut repliable */}
          <details className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="system-health-technical-details">
            <summary className="cursor-pointer text-sm font-medium text-slate-700" data-testid="system-health-technical-details-summary">
              Détails techniques ({usefulChecks.length + frontendChecks.length} checks utiles)
            </summary>
            <div className="mt-3 space-y-1.5">
              {frontendChecks.map((c) => (
                <div key={c.name} className="flex items-start gap-2 text-xs" data-testid={`system-health-check-${c.name.toLowerCase().replaceAll("_", "-")}`}>
                  <StatusIcon status={c.status} />
                  <code className="text-slate-700 font-mono flex-shrink-0">{c.name}</code>
                  <span className="text-slate-500">— {c.message}</span>
                </div>
              ))}
              {usefulChecks.map((c) => (
                <div key={c.name} className="flex items-start gap-2 text-xs" data-testid={`system-health-check-${c.name.toLowerCase().replaceAll("_", "-")}`}>
                  <StatusIcon status={c.status} />
                  <code className="text-slate-700 font-mono flex-shrink-0">{c.name}</code>
                  <span className="text-slate-500">— {c.message}</span>
                </div>
              ))}
            </div>
          </details>
        </>
      )}
    </div>
  );
}

function LiveMetricCard({ icon: Icon, label, value, sub, tone = "normal", testid }) {
  const toneClass = tone === "warning" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white";
  const iconClass = tone === "warning" ? "text-amber-600" : "text-emerald-600";
  return (
    <div className={`rounded-xl border ${toneClass} p-4 min-w-0`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
        <Icon className={`w-4 h-4 ${iconClass}`} />
        <span>{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900 truncate" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1 truncate" data-testid={`${testid}-sub`}>{sub}</div>
    </div>
  );
}

function HealthCard({ icon: Icon, title, status, detail, sub, testid }) {
  const colorMap = {
    ok: "border-emerald-200 bg-emerald-50",
    warning: "border-amber-200 bg-amber-50",
    error: "border-rose-200 bg-rose-50",
    unknown: "border-slate-200 bg-slate-50",
  };
  const iconColorMap = {
    ok: "text-emerald-600",
    warning: "text-amber-600",
    error: "text-rose-600",
    unknown: "text-slate-400",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border ${colorMap[status] || colorMap.unknown} p-4`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${iconColorMap[status] || iconColorMap.unknown}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
            <StatusIcon status={status} />
          </div>
          <p className="text-xs text-slate-600 mt-0.5" data-testid={`${testid}-detail`}>{detail}</p>
          {sub && <p className="text-[11px] text-slate-500 mt-1" data-testid={`${testid}-sub`}>{sub}</p>}
        </div>
      </div>
    </div>
  );
}
